import type { GraphLoadState } from 'features/knowledgeDocumentViewer/graph/useDocumentGraph'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { DocumentPreviewViewer } from './DocumentPreviewViewer'

const LightboxRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: var(--background);
  overflow: hidden;
  pointer-events: auto;
`

interface PreviewPageLightboxProps {
  thumbnailUrl: string | null
  hiResUrl: string | null
  isThumbLoading: boolean
  alt: string
  documentId: string
  currentPage: number
  totalPages: number
  highlightBoxes?: NormalizedBBox[]
  focusBoxes?: NormalizedBBox[]
  graph?: GraphLoadState
  onPageChange: (page: number) => void
  onClose: () => void
}

export const PreviewPageLightbox: React.FC<PreviewPageLightboxProps> = ({
  thumbnailUrl,
  hiResUrl,
  isThumbLoading,
  alt,
  documentId,
  currentPage,
  totalPages,
  highlightBoxes,
  focusBoxes,
  graph,
  onPageChange,
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <LightboxRoot role='dialog' aria-modal='true' aria-label={alt}>
      <DocumentPreviewViewer
        thumbnailUrl={thumbnailUrl}
        hiResUrl={hiResUrl}
        isThumbLoading={isThumbLoading}
        alt={alt}
        documentId={documentId}
        currentPage={currentPage}
        totalPages={totalPages}
        highlightBoxes={highlightBoxes}
        focusBoxes={focusBoxes}
        graph={graph}
        onPageChange={onPageChange}
        showResetZoom={false}
        onToggleFullscreen={onClose}
        isFullscreen
      />
    </LightboxRoot>,
    document.body
  )
}
